/**
 * Countdown days, hours, minutes and seconds
 * @module countdown
 */

import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR   = MINUTE * 60;
export const DAY    = HOUR * 24;

export const DAY_CONTAINER    = '.js-countdown-day';
export const HOUR_CONTAINER   = '.js-countdown-hour';
export const MINUTE_CONTAINER = '.js-countdown-minute';
export const SECOND_CONTAINER = '.js-countdown-second';
export const DAY_LABEL        = '.js-countdown-day-label';
export const HOUR_LABEL       = '.js-countdown-hour-label';
export const MINUTE_LABEL     = '.js-countdown-minute-label';
export const SECOND_LABEL     = '.js-countdown-second-label';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'countdown';
export const DEFAULTS = {
  targetDateTime:   'January 1, 2029 00:00:00 PST',
};

/**
 * Update countdown heading or remove when time expires
 * @param  {jQuery} $el - link
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const updateCountdownHeading = ($el, options = {}) => {
  // Returns the closest preceding sibling matching the selector
  const $countdownHed = $el.prevAll('.js-countdown__hed').first();
  // Get countdown heading's alt copy to replace with at end of countdown
  const countdownAltText = $countdownHed.data('countdown-alt-text');
  // If countdown heading exists and has countdown alt text attribute w/value
  if($countdownHed.length && $countdownHed.data('countdownAltText') !== undefined) {
    $countdownHed.text(countdownAltText);
  } else {
    // Remove countdown heading copy
    $countdownHed.html('');
  }
}
/**
 * Runs the countdown and sets the interval
 * @param  {jQuery} $el - link
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const runCountdown = ($el, options = {}) => {
  let target = new Date(options.targetDateTime).getTime();
  let intervalId = setInterval(() => {
    let now = new Date().getTime();
    const timeRemaining = target - now;
    let secondsRemaining = Math.floor(timeRemaining / 1000); // ms to seconds
    const $removeExpiredCountdown = $el.data('remove-expired-countdown'); // Accesses data-remove-expired-countdown
    const $countdownSansSeconds = $el.data('sans-seconds'); // Accesses data-sans-seconds
    // Check for the data attribute data-remove-expired-countdown
    // if set the countdown will be removed when it expires
    if ($removeExpiredCountdown === true || $removeExpiredCountdown === "") {
    // explicitly true or attribute is present without a value (true is emplied)

      // Check for the data attribute data-sans-seconds if set
      // this will remove the coundown clock w/ 60 seconds left
      // prevents displaying 0d:0h:0m
      if ($countdownSansSeconds === true || $countdownSansSeconds === "") {
      // explicitly true or attribute is present without a value (true is emplied)

        if (secondsRemaining <= 60) {
          clearInterval(intervalId);
          secondsRemaining = 0;
          updateCountdownHeading($el, options);
          $el.html('');
        }
      } else {
        if (secondsRemaining <= 0) {
          clearInterval(intervalId);
          secondsRemaining = 0;
          updateCountdownHeading($el, options);
          $el.html('');
        }
      }
    } else {
      if (secondsRemaining <= 0) {
        clearInterval(intervalId);
        secondsRemaining = 0;
        updateCountdownHeading($el, options);
      }
    }

    updateCountdownDisplay($el, options, timeRemaining);
  }, SECOND);
}

/**
 * Updates the countdown display elements
 * @param  {jQuery} $el - link
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {number} secondsLeft - amount of time still left in the countdown
 */
const updateCountdownDisplay = ($el, options = {}, timeRemaining) => {
  let daysLeft    = Math.floor(timeRemaining / (DAY));
  let hoursLeft   = Math.floor((timeRemaining % (DAY)) / (HOUR));
  let minutesLeft = Math.floor((timeRemaining % (HOUR)) / (MINUTE));
  let secondsLeft = Math.floor((timeRemaining % (MINUTE)) / SECOND);

  $el.find(DAY_CONTAINER).text(daysLeft);
  $el.find(HOUR_CONTAINER).text(hoursLeft);
  $el.find(MINUTE_CONTAINER).text(minutesLeft);
  $el.find(SECOND_CONTAINER).text(secondsLeft);

  const $dayLabel = $el.find(DAY_LABEL);
  const $hoursLabel = $el.find(HOUR_LABEL);
  const $minuteLabel = $el.find(MINUTE_LABEL);
  const $secondsLabel = $el.find(SECOND_LABEL);

  if($dayLabel.data('day-abbr')) {
    $dayLabel.text('D');
  } else {
    $dayLabel.text((daysLeft === 1 ? 'day' : 'days'));
  }

  if($hoursLabel.data('hour-abbr')) {
    $hoursLabel.text('H');
  } else {
    $hoursLabel.text((hoursLeft % 24 === 1 ? 'hour' : 'hours'));
  }

  if($minuteLabel.data('minute-abbr')) {
    $minuteLabel.text('M');
  } else {
    $minuteLabel.text((minutesLeft % 60 === 1 ? 'minute' : 'minutes'));
  }
  if($secondsLabel.data('second-abbr')) {
    $secondsLabel.text('S');
  } else {
    $secondsLabel.text((secondsLeft % 60 === 1 ? 'second' : 'seconds'));
  }
}
/**
 * Initializes countdown
 * @param {HTMLElement} el - link
 * @param {Object} options - Options used for wiring up the modal
 * @see DEFAULTS
 */
export default function countdown(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  runCountdown($el, opts);
}
registerJQueryPlugin(PLUGIN_NAME, countdown);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/countdown.js